import { Box, useTheme } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusDot } from "./renderers/StatusDot";
import { getM3Colors, getM3Elevation } from "../../theme/style/m3Palette";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import { LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight } from "react-icons/lu";
import { MenuItem, Select, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";

const CheckboxEditor = forwardRef((props, ref) => {
  const [checked, setChecked] = useState(!!props.value);
  useEffect(() => { setChecked(!!props.value); }, [props.value]);
  useImperativeHandle(ref, () => ({ getValue: () => (checked ? 1 : 0) }));
  return <input type="checkbox" checked={props.value} readOnly />;
});

const isRowEditable = (params) => {
  const colDefs = params.api.getColumnDefs();
  return colDefs.some((colDef) => {
    if (!colDef.field) return false;
    const editable = colDef.editable;
    if (typeof editable === "function") return editable({ ...params, colDef });
    return !!editable;
  });
};

const CustomTooltip = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const m3 = getM3Colors(isDark);
  const brandGreen = m3.primary;

  return (
    <div
      style={{
        backgroundColor: isDark ? m3.surfaceContainerHigh : m3.surfaceContainerLowest,
        color: m3.onSurface,
        padding: "12px 16px",
        borderRadius: "8px",
        border: `1px solid ${brandGreen}`,
        boxShadow: getM3Elevation(isDark)[2],
        fontSize: "13px",
        lineHeight: "1.6",
        maxWidth: "450px",
        pointerEvents: "none",
        zIndex: 9999,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: "6px", color: brandGreen, display: 'flex', alignItems: 'center', gap: '8px' }}>
        {props.colDef.headerName}
      </div>
      <div style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
        {props.value}
      </div>
    </div>
  );
};

const AgGrid = forwardRef(
  (
    {
      rowData = [],
      columnDefs = [],
      onSelectionChanged = () => { },
      handleContextMenu = () => { },
      headerCheckbox = false,
      checkboxes = false,
      getRowId = (params) => String(params.data.id),
      onCellValueChanged = () => { },
      pinnedBottomRowData = [],
      onColumnMoved = () => { },
      components = {},
      colVisibility_key = null,
      domLayout = "normal",
      onRowClicked = () => { },
      sx = {},
      suppressPagination = false,
      pagination = true,
      paginationPageSize = "All",
      paginationPageSizeSelector,
      pageSizeOptions = [5, 10, 25, 50, 100, "All"],
      sideBar = false,
      defaultColDef = {},
      footerLeftContent = null,
      footerRightContent = null,
      quickFilterText = "",
      maxHeight = null,
      singleClickEdit = false,
      hidePaginationControls = false,
      getRowStyle = null,
      visibleRows = undefined,
      context = undefined,
      rowHeight = 48,
      getRowHeight = null,
    },
    ref
  ) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const m3 = getM3Colors(isDark);
    const tok = getOrgTokens(isDark);

    const brandGreen = m3.primary;

    const prevSelectedIdsRef = useRef(new Set());

    const handleCellClicked = (params) => {
      const { node, colDef, column, api } = params;
      const isEditable = typeof colDef.editable === "function" ? colDef.editable(params) : colDef.editable;
      
      if (singleClickEdit) {
        if (isEditable) {
          api.startEditingCell({ rowIndex: node.rowIndex, colKey: column.getColId() });
        }
      } else {
        const wasAlreadySelected = prevSelectedIdsRef.current.has(node.id);
        if (wasAlreadySelected && isEditable) {
          api.startEditingCell({ rowIndex: node.rowIndex, colKey: column.getColId() });
        }
      }
    };

    const handleSelectionChanged = (event) => {
      const selectedRows = event.api.getSelectedRows();
      prevSelectedIdsRef.current = new Set(selectedRows.map((r) => r.id));
      onSelectionChanged(event);
    };

    const onGridReady = (params) => {
      // Keep effectivePageSize in sync with grid options
      params.api.addEventListener("paginationChanged", () => {
        const newSize = params.api.getGridOption("paginationPageSize");
        setEffectivePageSize(newSize);
      });

      if (colVisibility_key) {
        const widthKey = `${colVisibility_key}_widths`;
        const savedWidths = localStorage.getItem(widthKey);
        if (savedWidths) {
          setTimeout(() => {
            try {
              const columnWidths = JSON.parse(savedWidths);
              const columnState = params.api.getColumnState();
              const updatedState = columnState.map((col) => {
                const savedCol = columnWidths[col.colId];
                if (savedCol) {
                  const width = typeof savedCol === "number" ? savedCol : savedCol.width;
                  const flex = typeof savedCol === "number" ? null : savedCol.flex;
                  return { ...col, width, flex };
                }
                return col;
              });
              params.api.applyColumnState({ state: updatedState, applyOrder: true });
            } catch (e) {
              console.error("Error applying saved column widths:", e);
            }
          }, 100);
        }
      }
    };

    const onColumnResized = (params) => {
      if (params.finished && params.source === "uiColumnDragged" && colVisibility_key) {
        const widthKey = `${colVisibility_key}_widths`;
        const columnState = params.api.getColumnState();
        const columnWidths = {};
        columnState.forEach((col) => {
          if (col.colId && col.width) {
            columnWidths[col.colId] = { width: col.width, flex: col.flex };
          }
        });
        localStorage.setItem(widthKey, JSON.stringify(columnWidths));
      }
    };

    const internalGridRef = useRef(null);

    const resolvedPageSizeOptions = useMemo(() => {
      const options = paginationPageSizeSelector || pageSizeOptions;
      return options.map(opt => (typeof opt === 'string' && opt.toLowerCase() === 'all') ? 'All' : opt);
    }, [paginationPageSizeSelector, pageSizeOptions]);

    const stableDefaultColDef = useMemo(() => ({
      filter: false,
      suppressHeaderMenuButton: true,
      ...defaultColDef
    }), [defaultColDef]);

    const customIcons = useMemo(() => ({
      menu: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="12" x2="20" y2="12"></line><line x1="4" y1="6" x2="20" y2="6"></line><line x1="4" y1="18" x2="20" y2="18"></line></svg>`,
      filter: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="12" x2="20" y2="12"></line><line x1="4" y1="6" x2="20" y2="6"></line><line x1="4" y1="18" x2="20" y2="18"></line></svg>`,
    }), []);

    const [effectivePageSize, setEffectivePageSize] = useState(paginationPageSize);

    useEffect(() => {
      setEffectivePageSize(paginationPageSize);
    }, [paginationPageSize]);

    useImperativeHandle(ref, () => ({
      get api() { return internalGridRef.current?.api; },
      get columnApi() { return internalGridRef.current?.columnApi; }
    }));

    const [contentHeight, setContentHeight] = useState(0);
    const [isOverMax, setIsOverMax] = useState(false);

    const targetRowCount = visibleRows || 10;

    const defaultContainerHeight = useMemo(() => {
      const rowHeight = 48;
      const headerHeight = 48;
      const footerHeight = (pagination && !suppressPagination) ? 72 : 0;
      const scrollbarHeight = 18;
      return (targetRowCount * rowHeight) + headerHeight + footerHeight + scrollbarHeight + 4;
    }, [targetRowCount, pagination, suppressPagination]);

    const resolvedHeight = useMemo(() => {
      if (maxHeight !== undefined && maxHeight !== null) {
        return typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
      }
      return `${defaultContainerHeight}px`;
    }, [maxHeight, defaultContainerHeight]);

    const onRowDataChanged = useCallback((params) => {
      const api = params.api;
      if (!api) return;
      
      let rowsToDisplay = api.getDisplayedRowCount();
      
      if (pagination && !suppressPagination) {
        const pageSize = api.paginationGetPageSize();
        const currentPage = api.paginationGetCurrentPage();
        
        const startRow = currentPage * pageSize;
        const endRow = Math.min(startRow + pageSize, rowsToDisplay);
        rowsToDisplay = Math.max(0, endRow - startRow);
      }
      
      const rowHeight = 48;
      const headerHeight = 48;
      const footerHeight = (pagination && !suppressPagination) ? 72 : 0;
      
      const effectiveRows = Math.max(1, rowsToDisplay); // Ensure at least 1 row of space for empty overlay
      const scrollbarHeight = 18; // Reserve space for the horizontal scrollbar (from our CSS)
      const total = (effectiveRows * rowHeight) + headerHeight + footerHeight + scrollbarHeight + 4;
      
      setContentHeight(total);
      
      const maxH = typeof resolvedHeight === 'string' && resolvedHeight.includes('px') 
        ? parseInt(resolvedHeight) 
        : (typeof resolvedHeight === 'number' ? resolvedHeight : defaultContainerHeight);
      
      setIsOverMax(total > maxH);
    }, [pagination, suppressPagination, resolvedHeight, defaultContainerHeight]);

    const computedDomLayout = useMemo(() => {
      if (domLayout === "autoHeight") return "autoHeight";
      return "normal"; // Forced normal layout to guarantee fixed headers and internal scrollbars
    }, [domLayout]);

    const gridHeight = useMemo(() => {
      if (computedDomLayout === "autoHeight") return "auto";
      return resolvedHeight;
    }, [computedDomLayout, resolvedHeight]);

    const stableColumnDefs = useMemo(() => {
      const formatCol = (col) => {
        const formatted = {
          ...col,
          headerName: t(col.headerName || "Header Name"),
        };
        if (col.children && Array.isArray(col.children)) {
          formatted.children = col.children.map(formatCol);
        } else {
          formatted.cellClassRules = {
            ...(col.cellClassRules || {}),
            "non-editable-cell": (params) =>
              !params.colDef.editable ||
              (typeof params.colDef.editable === "function" && !params.colDef.editable(params)),
          };
        }
        return formatted;
      };

      return columnDefs.map(formatCol);
    }, [columnDefs, t, i18n.language]);

    const stableFrameworkComponents = useMemo(() => ({
      checkboxEditor: CheckboxEditor,
      statusDot: StatusDot,
      customTooltip: CustomTooltip,
      ...components,
    }), [components]);

    return (
      <Box
        className="ag-theme-alpine grid-container"
        onContextMenu={handleContextMenu}
        sx={{
          height: computedDomLayout === "autoHeight" ? "auto" : resolvedHeight,
          width: "100%",
          display: "flex",
          flexDirection: "column",

          // ── Core background & text colors ──────────────────────────────
          "--ag-background-color": tok.bg.card,
          "--ag-foreground-color": tok.text.gridCell,
          "--ag-header-background-color": tok.bg.tableHeader,
          "--ag-row-hover-color": "rgba(98, 227, 128, 0.18)",
          "--ag-odd-row-background-color": tok.bg.card,
          "--ag-selected-row-background-color": "#e8d49b",
          "--ag-pinned-column-background-color": tok.bg.card,

          // ── Borders: only thin horizontal row separators ───────────────
          "--ag-border-color": tok.border.table,
          "--ag-row-border-color": tok.border.row,
          "--ag-secondary-border-color": "transparent",
          "--ag-header-column-separator-display": "none",
          // Keep resize handles visible so user can drag column widths
          "--ag-header-column-resize-handle-display": "block",
          "--ag-header-column-resize-handle-color": "rgba(0, 0, 0, 0.12)",
          "--ag-header-column-resize-handle-height": "50%",
          "--ag-header-column-resize-handle-width": "1px",
          "--ag-borders": "none",
          "--ag-focus-grid-cell-border-color": "transparent",
          "--ag-range-selection-border-color": "transparent",

          // ── Typography ─────────────────────────────────────────────────
          "--ag-font-size": "15px",
          "--ag-font-family": '"Poppins", sans-serif',
          "--ag-header-foreground-color": tok.text.tableHeader || tok.text.secondary,

          // ── Sizing / Spacing ───────────────────────────────────────────
          "--ag-cell-horizontal-padding": "16px",
          "--ag-row-height": "48px",
          "--ag-header-height": "48px",

          // ── Scrollbars (GaDDi Clean Rounded) ──────────────────────────
          "& .ag-body-horizontal-scroll, & .ag-body-horizontal-scroll-viewport": {
            height: "14px !important",
            minHeight: "14px !important",
            maxHeight: "14px !important",
          },
          "& ::-webkit-scrollbar": {
            width: "14px !important",
            height: "14px !important",
          },
          "& ::-webkit-scrollbar-track, & .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-track": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f1f2f4",
            borderRadius: "4px",
          },
          "& ::-webkit-scrollbar-thumb, & .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.20)" : "#c1c5cc",
            borderRadius: "4px",
            border: isDark ? "none" : "3px solid #f1f2f4",
          },
          "& ::-webkit-scrollbar-thumb:hover, & .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.35)" : "#a3a9b3",
          },

          // Standard viewport styling without RTL scrollbar mirroring hack to keep columns aligned
          "& .ag-body-viewport": {
            direction: "inherit !important",
          },
          "& .ag-body-viewport *": {
            direction: "inherit !important",
          },

          // ── Remove outer wrapper border ────────────────────────────────
          "& .ag-root-wrapper": {
            border: "none !important",
            borderRadius: "0 !important",
            height: "100% !important",
          },

          // ── Header (Clean Standard Styling) ────────────────────────────
          "& .ag-header": {
            borderBottom: `1px solid ${tok.border.table} !important`,
            position: "sticky !important",
            top: 0,
            zIndex: 10,
            backgroundColor: `${tok.bg.tableHeader} !important`,
          },
          "& .ag-header-row": {
            backgroundColor: `${tok.bg.tableHeader} !important`,
            borderBottom: "none !important",
          },
          "& .ag-header-viewport, & .ag-pinned-left-header, & .ag-pinned-right-header": {
            borderBottom: "none !important",
            backgroundColor: `${tok.bg.tableHeader} !important`,
          },
          "& .ag-header-cell, & .ag-header-group-cell": {
            backgroundColor: "transparent !important",
            borderRight: "none !important",
            borderBottom: "none !important",
            paddingLeft: "16px !important",
            paddingRight: "16px !important",
          },
          // All header labels are centred (AGENTS.md section 4), whatever the column's cell alignment
          "& .ag-header-cell-label, & .ag-header-group-cell-label": {
            justifyContent: "center !important",
            textAlign: "center !important",
          },
          "& .ag-header-cell::after, & .ag-header-cell::before": {
            display: "none !important",
          },
          "& .ag-header-cell-text, & .ag-header-cell-label": {
            fontSize: "14px !important",
            fontWeight: "700 !important",
            letterSpacing: "0.01em !important",
            textTransform: "none !important",
            color: `${tok.text.tableHeader || tok.text.secondary} !important`,
          },
          "& .ag-header-cell-resize::after": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.20)" : "rgba(0, 0, 0, 0.15) !important",
            width: "1px !important",
          },
          "& .ag-header-column-separator, & .ag-header-column-separator-part": {
            display: "none !important",
          },
          "& .ag-header-cell-menu-button, & .ag-header-cell-filter-button, & .ag-filter-icon": {
            display: "none !important",
          },

          // ── Cells ──────────────────────────────────────────────────────
          "& .ag-cell": {
            display: "flex !important",
            alignItems: "center",
            fontSize: "15px !important",
            fontWeight: 400,
            color: `${tok.text.primary} !important`,
            fontFamily: "'Poppins', sans-serif !important",
            overflow: "hidden !important",
            whiteSpace: "nowrap !important",
            textOverflow: "ellipsis !important",
          },
          "& .ag-cell-value": {
            display: "block !important",
            fontSize: "15px !important",
            fontFamily: "'Poppins', sans-serif !important",
            overflow: "hidden !important",
            textOverflow: "ellipsis !important",
            whiteSpace: "nowrap !important",
            width: "100%",
          },
          // Remove any outlines when a cell stays selected (GaDDi kill cell focus)
          "& .ag-cell-focus, & .ag-cell-range-selected, & .ag-cell-range-single-cell": {
            border: "none !important",
            outline: "none !important",
            boxShadow: "none !important",
          },

          // ── Cell Editor (Seamless Editing) ──────────────────────────────
          "& .ag-cell.ag-cell-inline-editing": {
            padding: "0 !important",
            outline: "none !important",
          },
          "& .ag-cell-edit-wrapper": {
            height: "100% !important",
            width: "100% !important",
            display: "flex !important",
            alignItems: "center !important",
          },
          "& .ag-cell-editor": {
            height: "100% !important",
            width: "100% !important",
            border: "none !important",
            backgroundColor: "transparent !important",
            display: "flex !important",
            alignItems: "center !important",
          },
          "& .ag-cell-editor input, & .ag-cell-editor select, & .ag-cell-editor .ag-input-field-input": {
            height: "100% !important",
            width: "100% !important",
            padding: "0 16px !important",
            margin: "0 !important",
            border: `1px solid ${tok.border.table} !important`, // Standard subtle border
            borderRadius: "0 !important",
            fontSize: "14px !important",
            fontFamily: "inherit !important",
            outline: "none !important",
            backgroundColor: `${tok.bg.card} !important`,
            boxSizing: "border-box !important",
            boxShadow: "none !important",
          },

          // ── Rows (GaDDi Selection & Hover Polish) ───────────────────────
          "& .ag-row": {
            borderBottom: `1px solid ${tok.border.row} !important`,
            transition: "background-color 150ms ease",
          },
          "& .ag-row:hover, & .ag-row:hover .ag-cell": {
            backgroundColor: "rgba(98, 227, 128, 0.18) !important",
          },
          "& .ag-row.ag-row-selected, & .ag-row-selected, & .ag-row.ag-row-selected .ag-cell, & .ag-row-selected .ag-cell": {
            backgroundColor: "#e8d49b !important",
            color: "#1a3a4d !important",
          },
          "& .ag-row.ag-row-selected:hover, & .ag-row-selected:hover, & .ag-row.ag-row-selected:hover .ag-cell": {
            backgroundColor: "#e8d49b !important",
          },
          "& .ag-row .non-editable-cell": {
            backgroundColor: "transparent !important",
          },
          "& .ag-row.ag-row-last": {
            borderBottom: "none !important",
          },

          // ── Hide built-in pagination ───────────────────────────────────
          "& .ag-paging-panel": {
            display: "none !important",
          },

          // ── CIRCULAR selection checkboxes (matches the reference design) ─
          "& .ag-checkbox-input-wrapper": {
            width: "18px !important",
            height: "18px !important",
            minWidth: "18px !important",
            minHeight: "18px !important",
            borderRadius: "50% !important",
            border: `1.5px solid ${brandGreen} !important`,
            backgroundColor: "transparent !important",
            boxShadow: "none !important",
            overflow: "hidden !important",
            display: "flex !important",
            alignItems: "center !important",
            justifyContent: "center !important",
          },
          "& .ag-checkbox-input-wrapper::after": {
            display: "none !important",
          },
          "& .ag-checkbox-input-wrapper.ag-checked": {
            backgroundColor: `${brandGreen} !important`,
            border: `1.5px solid ${brandGreen} !important`,
          },
          "& .ag-checkbox-input-wrapper.ag-checked::after": {
            display: "flex !important",
            color: `${m3.onPrimary} !important`,
          },
          "& .ag-checkbox-input-wrapper.ag-indeterminate": {
            backgroundColor: `${brandGreen} !important`,
            border: `1.5px solid ${brandGreen} !important`,
          },
          "& .ag-checkbox-input-wrapper.ag-indeterminate::after": {
            display: "flex !important",
            color: `${m3.onPrimary} !important`,
          },
          // Selection column width
          "& .ag-selection-checkbox": {
            marginRight: "0 !important",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },

          // ── Filter Popup Styling ───────────────────────────────────────
          "& .ag-filter-wrapper": {
            width: "220px !important",
            padding: "12px !important",
            borderRadius: "8px !important",
            border: `1px solid ${m3.outlineVariant} !important`,
            boxShadow: `${getM3Elevation(isDark)[2]} !important`,
            backgroundColor: `${tok.bg.card} !important`,
            fontFamily: '"Poppins", sans-serif !important',
          },
          "& .ag-simple-filter-body-wrapper": {
            display: "flex !important",
            flexDirection: "column !important",
            gap: "8px !important",
            width: "100% !important",
          },
          "& .ag-filter-picker": {
            marginBottom: "8px !important",
          },
          "& .ag-filter-field-button": {
            borderRadius: "6px !important",
          },
          "& .ag-filter-body-wrapper .ag-input-field-input": {
            width: "100% !important",
            borderRadius: "6px !important",
            height: "38px !important",
            padding: "0 10px !important",
            border: `1px solid ${m3.outlineVariant} !important`,
            backgroundColor: `${tok.bg.card} !important`,
            fontSize: "13px !important",
          },
          "& .ag-filter-body-wrapper select, & .ag-filter-body-wrapper .ag-picker-field-wrapper": {
            width: "100% !important",
            borderRadius: "6px !important",
            height: "38px !important",
            border: `1px solid ${m3.outlineVariant} !important`,
            backgroundColor: `${tok.bg.card} !important`,
          },

          ...sx,
        }}
      >
        <Box sx={{ height: computedDomLayout === "autoHeight" ? "auto" : (pagination && !suppressPagination ? "calc(100% - 72px)" : "100%") }}>
          <AgGridReact
            ref={internalGridRef}
            rowSelection={{
              mode: "multiRow",
              headerCheckbox,
              checkboxes,
              enableClickSelection: true,
            }}
            pagination={pagination}
            paginationPageSize={paginationPageSize === "All" || paginationPageSize === "all" ? 9999 : paginationPageSize}
            paginationPageSizeSelector={resolvedPageSizeOptions.map(opt => opt === "All" ? "all" : opt)}
            sideBar={sideBar}
            defaultColDef={stableDefaultColDef}
            icons={customIcons}
            quickFilterText={quickFilterText}
            suppressPaginationPanel={true}
            stopEditingWhenCellsLoseFocus={false}
            popupParent={document.body}
            onCellClicked={handleCellClicked}
            selectionColumnDef={{
              maxWidth: 52,
              minWidth: 52,
              width: 52,
              cellStyle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingLeft: "16px",
                paddingRight: "0",
              },
              headerClass: "ag-selection-header",
            }}
            columnDefs={stableColumnDefs}
            rowData={rowData}
            rowHeight={getRowHeight ? undefined : rowHeight}
            getRowHeight={getRowHeight || undefined}
            domLayout={computedDomLayout}
            onSelectionChanged={handleSelectionChanged}
            getRowId={getRowId}

            onCellValueChanged={onCellValueChanged}
            pinnedBottomRowData={pinnedBottomRowData}
            getRowStyle={(params) => {
              if (params.node.rowPinned === "bottom") return { fontWeight: "bold" };
              if (getRowStyle) {
                return getRowStyle(params);
              }
              return {};
            }}
            enableCellTextSelection={true}
            onColumnMoved={onColumnMoved}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            onModelUpdated={onRowDataChanged}
            onPaginationChanged={onRowDataChanged}
            tooltipShowDelay={400}
            frameworkComponents={stableFrameworkComponents}
            context={context}
          />
        </Box>
        {pagination && !suppressPagination && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              minHeight: "72px",
              px: 3,
              borderTop: `1px solid ${tok.border.table}`,
              backgroundColor: tok.bg.card,
              width: "100%",
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              {footerLeftContent}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {!hidePaginationControls && (
                <GridPagination gridRef={internalGridRef} pageSizeOptions={resolvedPageSizeOptions} />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {footerRightContent}
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);

export const GridPageSize = ({ api, gridRef, pageSizeOptions = [5, 10, 25, 50, 100, "All"] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const m3 = getM3Colors(isDark);
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState("All");
  const [gridApi, setGridApi] = useState(api);

  const getApi = () => api || gridRef?.current?.api || gridRef?.api;

  useEffect(() => {
    if (gridApi) return;
    const resolvedApi = getApi();
    if (resolvedApi) {
      setGridApi(resolvedApi);
      return;
    }
    const timer = setInterval(() => {
      const currentApi = getApi();
      if (currentApi) {
        setGridApi(currentApi);
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, [api, gridRef, gridApi]);

  useEffect(() => {
    if (!gridApi) return;
    const update = () => {
      const currentSize = gridApi.getGridOption("paginationPageSize");
      const rc = gridApi.getDisplayedRowCount();
      if (currentSize >= (rc || 9999)) {
        setPageSize("All");
      } else {
        setPageSize(currentSize);
      }
    };
    gridApi.addEventListener("paginationChanged", update);
    update();
    return () => gridApi.removeEventListener("paginationChanged", update);
  }, [gridApi]);

  const handlePageSizeChange = (event) => {
    const val = event.target.value;
    if (val === "All") {
      const rc = gridApi?.getDisplayedRowCount() || 9999;
      gridApi?.setGridOption("paginationPageSize", rc);
      setPageSize("All");
    } else {
      const newSize = Number(val);
      setPageSize(newSize);
      gridApi?.setGridOption("paginationPageSize", newSize);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: m3.onSurfaceVariant, fontFamily: "'Poppins', sans-serif" }}>{t("Page Size:")}</Typography>
      <Select
        value={pageSize}
        onChange={handlePageSizeChange}
        variant="standard"
        sx={{
          fontSize: "13px",
          fontWeight: 700,
          fontFamily: "'Poppins', sans-serif",
          color: m3.onSurface,
          "& .MuiSelect-select": { py: 0.5, pr: "20px !important" },
          "&:before, &:after": { border: "none !important" },
        }}
        disableUnderline
      >
        {pageSizeOptions.map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export const GridPagination = ({ api, gridRef, hidePageSize = false, pageSizeOptions = [5, 10, 25, 50, 100, "All"] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const m3 = getM3Colors(isDark);
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState("All");
  const [rowCount, setRowCount] = useState(0);
  const [gridApi, setGridApi] = useState(api);

  const getApi = () => api || gridRef?.current?.api || gridRef?.api;

  useEffect(() => {
    if (gridApi) return;
    const resolvedApi = getApi();
    if (resolvedApi) {
      setGridApi(resolvedApi);
      return;
    }

    const timer = setInterval(() => {
      const currentApi = getApi();
      if (currentApi) {
        setGridApi(currentApi);
        clearInterval(timer);
      }
    }, 100);

    const timeout = setTimeout(() => clearInterval(timer), 5000);

    return () => {
      clearInterval(timer);
      clearTimeout(timeout);
    };
  }, [gridRef, api, gridApi]);

  useEffect(() => {
    if (!gridApi) return;

    const updateLabel = () => {
      setCurrentPage(gridApi.paginationGetCurrentPage() + 1);
      setTotalPages(gridApi.paginationGetTotalPages());
      const rc = gridApi.getDisplayedRowCount();
      setRowCount(rc);
      
      const currentSize = gridApi.getGridOption("paginationPageSize");
      if (currentSize >= (rc || 9999)) {
        setPageSize("All");
      } else {
        setPageSize(currentSize);
      }
    };

    gridApi.addEventListener("paginationChanged", updateLabel);
    gridApi.addEventListener("modelUpdated", updateLabel);
    updateLabel();

    return () => {
      gridApi.removeEventListener("paginationChanged", updateLabel);
      gridApi.removeEventListener("modelUpdated", updateLabel);
    };
  }, [gridApi]);

  const handlePageSizeChange = (event) => {
    const val = event.target.value;
    if (val === "All") {
      gridApi?.setGridOption("paginationPageSize", rowCount || 9999);
      setPageSize("All");
    } else {
      const newSize = Number(val);
      setPageSize(newSize);
      gridApi?.setGridOption("paginationPageSize", newSize);
    }
  };

  const brandGreen = m3.primary;

  return (
    <Box sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 3,
      color: m3.onSurface,
      fontFamily: "'Poppins', sans-serif"
    }}>
      {!hidePageSize && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: m3.onSurfaceVariant, fontFamily: "inherit" }}>{t("Page Size:")}</Typography>
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            variant="standard"
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              fontFamily: "inherit",
              color: m3.onSurface,
              "& .MuiSelect-select": { py: 0.5, pr: "20px !important" },
              "&:before, &:after": { border: "none !important" },
            }}
            disableUnderline
          >
            {pageSizeOptions.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton size="small" onClick={() => gridApi?.paginationGoToFirstPage()} disabled={!gridApi || currentPage === 1} sx={{ color: brandGreen, opacity: (!gridApi || currentPage === 1) ? 0.3 : 1 }}>
          <LuChevronsLeft size={16} />
        </IconButton>
        <IconButton size="small" onClick={() => gridApi?.paginationGoToPreviousPage()} disabled={!gridApi || currentPage === 1} sx={{ color: brandGreen, opacity: (!gridApi || currentPage === 1) ? 0.3 : 1 }}>
          <LuChevronLeft size={16} />
        </IconButton>

        <Typography sx={{ fontSize: "13px", mx: 1.5, whiteSpace: "nowrap", color: m3.onSurfaceVariant, fontFamily: "inherit", display: "flex", alignItems: "center" }}>
          {t("Page")}
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "32px",
              height: "28px",
              border: `1px solid ${m3.outlineVariant}`,
              borderRadius: "4px",
              fontWeight: 700,
              color: m3.onSurface,
              backgroundColor: m3.surfaceContainer,
              mx: 1,
              px: 1,
            }}
          >
            {currentPage}
          </Box>
          {t("of")}
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              color: m3.onSurface,
              ml: 1,
            }}
          >
            {totalPages || 1}
          </Box>
        </Typography>

        <IconButton size="small" onClick={() => gridApi?.paginationGoToNextPage()} disabled={!gridApi || currentPage === totalPages} sx={{ color: brandGreen, opacity: (!gridApi || currentPage === totalPages) ? 0.3 : 1 }}>
          <LuChevronRight size={16} />
        </IconButton>
        <IconButton size="small" onClick={() => gridApi?.paginationGoToLastPage()} disabled={!gridApi || currentPage === totalPages} sx={{ color: brandGreen, opacity: (!gridApi || currentPage === totalPages) ? 0.3 : 1 }}>
          <LuChevronsRight size={16} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default memo(AgGrid);